import { Quat, Vec3 } from 'playcanvas';

import { AnimTrack } from './anim-track';
import { AnimTrackEditOp } from './edit-ops';
import { Element, ElementType } from './element';
import { Events } from './events';
import { Model } from './model';
import { AnimatableElement, ObjectAnimTrack, ObjectKey } from './object-anim-track';
import { Scene } from './scene';
import { SceneObject } from './scene-object';
import { Splat } from './splat';
import { Water } from './water';

// which array (and index within it) an animated element serializes against -
// matches the ordering doc.ts already uses for splats/models/objects, plus
// water's own array, so tracks can be re-attached after those are reloaded
type TrackRef = { kind: 'splat' | 'model' | 'object' | 'water', index: number };

/**
 * Manages the active animation track and provides undo-wrapped
 * key operations. Resolves which track the user is interacting
 * with and ensures all mutations are undoable.
 *
 * The active track is the currently selected object's track (created lazily
 * on its first keyframe), or the camera track when nothing is selected.
 * Every object track - not just the active one - evaluates on every timeline
 * frame/time event (each ObjectAnimTrack subscribes to those internally), so
 * all animated objects play back together regardless of what's selected.
 */
const registerTrackManagerEvents = (events: Events, scene: Scene) => {
    const objectTracks = new Map<Element, ObjectAnimTrack>();

    const getSelected = (): AnimatableElement | null => {
        return (events.invoke('selection') as AnimatableElement) ?? null;
    };

    // Look up (without creating) the track for whatever is selected, or the
    // camera track when nothing is selected.
    const getExistingTrack = (): AnimTrack | null => {
        const el = getSelected();
        if (el) {
            return objectTracks.get(el) ?? null;
        }
        return events.invoke('camera.animTrack') ?? null;
    };

    // Same, but creates an object track on first use.
    const getOrCreateTrack = (): AnimTrack | null => {
        const el = getSelected();
        if (el) {
            let track = objectTracks.get(el);
            if (!track) {
                track = new ObjectAnimTrack(events, el);
                objectTracks.set(el, track);
            }
            return track;
        }
        return events.invoke('camera.animTrack') ?? null;
    };

    // Helper: execute an edit on the given track wrapped in undo.
    // The editFn must return true if it modified the track, false if it was a no-op.
    const trackEdit = (track: AnimTrack | null, name: string, editFn: (track: AnimTrack) => boolean) => {
        if (!track) return;
        const before = track.snapshot();
        if (!editFn(track)) return;
        const after = track.snapshot();
        events.fire('edit.add', new AnimTrackEditOp(name, track, before, after), true);
    };

    // clean up an object's track (and its timeline subscriptions) when it's removed
    events.on('scene.elementRemoved', (element: Element) => {
        const track = objectTracks.get(element);
        if (track) {
            track.destroy();
            objectTracks.delete(element);
        }
    });

    events.on('scene.clear', () => {
        objectTracks.forEach(track => track.destroy());
        objectTracks.clear();
    });

    // expose the current object-track map for serialization
    events.function('track.objectTracks', () => objectTracks);

    // Get keys from active track
    events.function('track.keys', () => {
        return getExistingTrack()?.keys ?? [];
    });

    // Add key to active track (creates an object track on first use). visible,
    // when given, overrides the target's live visibility for this key - lets
    // the timeline key an object as hidden without needing it to be
    // selected-and-hidden at the same time (selection.ts deselects hidden
    // elements, so that combination can't otherwise occur).
    events.on('track.addKey', (frame?: number, visible?: boolean) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit(getOrCreateTrack(), 'addKey', track => track.addKey(keyFrame, visible));
    });

    // Remove key from active track
    events.on('track.removeKey', (frame?: number) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit(getExistingTrack(), 'removeKey', track => track.removeKey(keyFrame));
    });

    // Move key in active track
    events.on('track.moveKey', (fromFrame: number, toFrame: number) => {
        trackEdit(getExistingTrack(), 'moveKey', track => track.moveKey(fromFrame, toFrame));
    });

    // Copy key in active track
    events.on('track.copyKey', (fromFrame: number, toFrame: number) => {
        trackEdit(getExistingTrack(), 'copyKey', track => track.copyKey(fromFrame, toFrame));
    });

    // Serialization: locate an animated element's position within the same
    // per-kind arrays doc.ts uses to serialize splats/models/objects, and
    // water's own array, so tracks can be re-attached by (kind, index).
    const getModels = () => scene.getElementsByType(ElementType.model).filter(m => m instanceof Model) as Model[];
    const getObjects = () => scene.getElementsByType(ElementType.model).filter(m => m instanceof SceneObject) as SceneObject[];
    const getWaters = () => scene.elements.filter(e => e instanceof Water) as Water[];

    const classify = (el: Element): TrackRef | null => {
        if (el instanceof Splat) {
            const index = (events.invoke('scene.allSplats') as Splat[]).indexOf(el);
            return index === -1 ? null : { kind: 'splat', index };
        }
        if (el instanceof Water) {
            const index = getWaters().indexOf(el);
            return index === -1 ? null : { kind: 'water', index };
        }
        if (el instanceof SceneObject) {
            const index = getObjects().indexOf(el);
            return index === -1 ? null : { kind: 'object', index };
        }
        if (el instanceof Model) {
            const index = getModels().indexOf(el);
            return index === -1 ? null : { kind: 'model', index };
        }
        return null;
    };

    const resolve = (ref: TrackRef): AnimatableElement | null => {
        switch (ref.kind) {
            case 'splat': return (events.invoke('scene.allSplats') as Splat[])[ref.index] ?? null;
            case 'water': return getWaters()[ref.index] ?? null;
            case 'object': return getObjects()[ref.index] ?? null;
            case 'model': return getModels()[ref.index] ?? null;
            default: return null;
        }
    };

    events.function('docSerialize.objectTracks', (): any[] => {
        const result: any[] = [];
        objectTracks.forEach((track, el) => {
            const keys = track.getKeys();
            if (keys.length === 0) return;
            const ref = classify(el);
            if (!ref) return;
            result.push({
                kind: ref.kind,
                index: ref.index,
                keys: keys.map(k => ({
                    frame: k.frame,
                    position: [k.position.x, k.position.y, k.position.z],
                    rotation: [k.rotation.x, k.rotation.y, k.rotation.z, k.rotation.w],
                    scale: [k.scale.x, k.scale.y, k.scale.z],
                    visible: k.visible
                }))
            });
        });
        return result;
    });

    events.function('docDeserialize.objectTracks', (data: any[] = []) => {
        (data ?? []).forEach((entry) => {
            const el = resolve({ kind: entry.kind, index: entry.index });
            if (!el) return;

            let track = objectTracks.get(el);
            if (!track) {
                track = new ObjectAnimTrack(events, el);
                objectTracks.set(el, track);
            }

            const keys: ObjectKey[] = entry.keys.map((k: any) => ({
                frame: k.frame,
                position: new Vec3(k.position),
                rotation: new Quat(k.rotation),
                scale: new Vec3(k.scale),
                visible: k.visible
            }));
            track.loadKeys(keys);
        });
    });
};

export { registerTrackManagerEvents };
